import React, { forwardRef, ForwardedRef, useImperativeHandle, useState, useRef } from 'react';
import { Box } from '@mui/material';
import { useQuillEditor } from './hooks/useQuillEditor';
import ToolbarButtonsRenderer from './components/ToolbarButtonsRenderer';
import EmojiPicker from './components/EmojiPicker';
import { DEFAULT_EDITING_OPTIONS } from './constants/editorConstants';

import 'quill/dist/quill.snow.css';

interface WysiwygProps {
  value?: string;
  placeholder?: string;
  onChange: (html: string) => void;
  id: string;
  minHeight?: number;
  editingOptions?: string[];
  maxHeight?: string;
}

const Wysiwyg = forwardRef(
  (
    {
      value = '',
      placeholder,
      onChange,
      id,
      minHeight = 0,
      editingOptions = DEFAULT_EDITING_OPTIONS,
      maxHeight = '50vh',
    }: WysiwygProps,
    ref: ForwardedRef<any>,
  ) => {
    const [emojiAnchorEl, setEmojiAnchorEl] = useState<HTMLElement | null>(null);
    const editorContainerRef = useRef<HTMLDivElement>(null);

    const { quillRef, quillInstance, activeFormats, dynamicButtonRoots, focus, getEditor } =
      useQuillEditor({
        id,
        editingOptions,
        placeholder,
        value,
        onChange,
        onEmojiButtonClick: setEmojiAnchorEl,
      });

    useImperativeHandle(ref, () => ({
      focus,
      getEditor,
    }));

    const handleEmojiSelect = (emoji: any) => {
      if (quillInstance) {
        const range = quillInstance.getSelection();
        if (range) {
          quillInstance.insertText(range.index, emoji.native);
          quillInstance.setSelection(range.index + emoji.native.length, 0);
        } else {
          const length = quillInstance.getLength();
          quillInstance.insertText(length - 1, emoji.native);
          quillInstance.setSelection(length + emoji.native.length - 1, 0);
        }
      }
      setEmojiAnchorEl(null);
    };

    const handleEmojiClose = () => {
      setEmojiAnchorEl(null);
    };

    return (
      <>
        <Box
          ref={editorContainerRef}
          sx={(theme) => ({
            width: '100%',
            minHeight: minHeight || 200,
            maxHeight: maxHeight,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            '& .ql-container': {
              flex: 1,
              overflow: 'auto',
              border: 'none !important',
            },
            '& .ql-editor': {
              minHeight: minHeight || 200,
              fontSize: `${theme.typography.body1.fontSize} !important`,
              fontFamily: `${theme.typography.fontFamily} !important`,
              // Custom placeholder styling to match MUI TextField
              '&.ql-blank::before': {
                color: `${theme.palette.text.disabled} !important`,
                fontStyle: 'inherit !important',
              },
            },
            '& .ql-toolbar': {
              border: 'none !important',
              borderBottom: `1px solid ${theme.palette.divider} !important`,
              display: 'flex !important',
              alignItems: 'center !important',
              flexWrap: 'wrap !important',
              padding: '8px !important',
              minHeight: '56px',
              lineHeight: '1 !important',
              backgroundColor: theme.palette.background.paper,
              flexShrink: 0,
              '& > *': {
                verticalAlign: 'top !important',
                margin: '0 !important',
                lineHeight: '1 !important',
              },
            },
            '& .wysiwyg-emoji-button:hover': {
              backgroundColor: `${theme.palette.action.hover} !important`,
            },
          })}
        >
          <div ref={quillRef} id={id} />
        </Box>

        <ToolbarButtonsRenderer
          dynamicButtonRoots={dynamicButtonRoots}
          activeFormats={activeFormats}
          quillInstance={quillInstance}
        />

        <EmojiPicker
          anchorEl={emojiAnchorEl}
          onEmojiSelect={handleEmojiSelect}
          onClose={handleEmojiClose}
        />
      </>
    );
  },
);

Wysiwyg.displayName = 'Wysiwyg';

export default Wysiwyg;
